// Searching public skill marketplaces: which rows a source chip shows, what the chips
// say, and which results the workspace already holds. The pane only renders this.
import type {
  ImportedSkill,
  ImportedSkillFile,
  MarketplaceSkill,
  MarketplaceSource,
  MarketplaceSourceStatus,
  SkillFileUpload,
  Skill,
  SkillImportIdentifiers,
} from "$lib/plugins/agents/types.js";
import { isWorkspaceAdmin } from "$lib/workspace-role.js";
import type { WorkspaceRole } from "$lib/workspace-role.js";

export const MARKETPLACE_ORDER: readonly MarketplaceSource[] = [
  "anthropic",
  "skills-sh",
  "claude-plugins",
  "skillsmp",
];

export const MARKETPLACE_LABELS: Record<MarketplaceSource, string> = {
  anthropic: "Anthropic (official)",
  "skills-sh": "skills.sh",
  "claude-plugins": "claude-plugins.dev",
  skillsmp: "SkillsMP",
};

const LIST_FORMAT = new Intl.ListFormat("en-US", { type: "conjunction" });

/** "Anthropic (official), skills.sh, claude-plugins.dev, and SkillsMP". */
export const ALL_MARKETPLACES = LIST_FORMAT.format(
  MARKETPLACE_ORDER.map((s) => MARKETPLACE_LABELS[s]),
);

export const MIN_QUERY_LENGTH = 2;
// The relay counts graphemes after trimming, and refuses 1000+ UTF-16 units before counting.
export const MAX_QUERY_LENGTH = 100;
export const MAX_QUERY_UNITS = 1000;

const GRAPHEMES = new Intl.Segmenter("en", { granularity: "grapheme" });
export const SEARCH_DEBOUNCE_MS = 350;

export type SourceFilter = MarketplaceSource | "all";

// A fetched skill on its way into the create form; sourceUrl pins its commit. `files` are
// the scripts and resources next to its SKILL.md, saved with it.
export interface MarketplaceDraft {
  name: string;
  slug: string;
  description: string;
  content: string;
  sourceUrl: string;
  files: ImportedSkillFile[];
  skippedFiles?: number;
}

export function draftFromImport(got: ImportedSkill, sourceUrl: string): MarketplaceDraft {
  return {
    name: got.name,
    slug: got.slug,
    description: got.description ?? "",
    content: got.content,
    sourceUrl: got.sourceUrl || sourceUrl,
    files: got.files ?? [],
    ...(got.skippedFiles ? { skippedFiles: got.skippedFiles } : {}),
  };
}

/** What upsert_skill saves: the files without their display-only size. */
export function filesToSave(files: readonly ImportedSkillFile[]): SkillFileUpload[] {
  return files.map(({ path, content, encoding, mode }) => ({ path, content, encoding, mode }));
}

// A result's importUrl names its repo, ref and folder; sending those instead of the URL
// imports the whole folder (scripts and resources), not SKILL.md alone. Accepts the raw
// form marketplaces return and the github.com blob form.
export function importIdentifiers(
  result: MarketplaceSkill,
): SkillImportIdentifiers | string | undefined {
  if (!result.importUrl) return undefined;
  let url: URL;
  try {
    url = new URL(result.importUrl);
  } catch {
    // The relay only emits parseable URLs; anything else is simply not importable as a folder.
    return undefined;
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const raw = url.hostname === "raw.githubusercontent.com";
  const blob = url.hostname === "github.com" && segments[2] === "blob";
  if (!raw && !blob) return undefined;
  const [owner, repo, ...rest] = segments;
  const tail = blob ? rest.slice(1) : rest;
  if (
    tail[0] === "refs" &&
    (tail[1] === "heads" || tail[1] === "tags") &&
    tail.at(-1) === "SKILL.md"
  ) {
    // A blob URL asks the relay to resolve the ref and import the whole folder.
    return `https://github.com/${owner}/${repo}/blob/${tail.join("/")}`;
  }
  const [ref, ...path] = tail;
  if (!owner || !repo || !ref || path[path.length - 1] !== "SKILL.md") return undefined;
  const dir = path.slice(0, -1).join("/");
  return { repo: `${owner}/${repo}`, ref, ...(dir ? { path: dir } : {}) };
}

// A relay that predates folder imports refuses the identifiers with this message.
export function isOldRelayImportRefusal(message: string): boolean {
  return message === "a GitHub URL is required";
}

/** Visible characters: "🙂" is 1, "👍🏽" is 1. */
export function graphemeCount(text: string): number {
  let n = 0;
  for (const _ of GRAPHEMES.segment(text)) n++;
  return n;
}

/** Trimmed, and cut to what the relay accepts: 100 graphemes within 1000 UTF-16 units. */
export function normalizeQuery(query: string): string {
  let out = "";
  let n = 0;
  for (const { segment } of GRAPHEMES.segment(query.trim())) {
    if (n === MAX_QUERY_LENGTH || out.length + segment.length > MAX_QUERY_UNITS) break;
    out += segment;
    n++;
  }
  return out.trimEnd();
}

export function searchReady(query: string): boolean {
  return graphemeCount(normalizeQuery(query)) >= MIN_QUERY_LENGTH;
}

/** Something typed, but not enough to send. */
export function queryTooShort(query: string): boolean {
  const n = graphemeCount(normalizeQuery(query));
  return n > 0 && n < MIN_QUERY_LENGTH;
}

export interface SearchFailure {
  text: string;
  // False when retrying now would only hit the limit again.
  retryable: boolean;
}

export function searchFailure(message: string, code?: string): SearchFailure {
  if (code === "rate_limited" || /too many searches/i.test(message)) {
    return { text: "Too many searches, try again in a minute", retryable: false };
  }
  return { text: message || "Search failed", retryable: true };
}

// Workspace writes (and the SKILL.md fetch behind a preview) need manage_agents.
export function canManageSkills(role: WorkspaceRole | null | undefined): boolean {
  return isWorkspaceAdmin(role);
}

export function listedIn(result: MarketplaceSkill, source: MarketplaceSource): boolean {
  return result.source === source || result.alsoIn.includes(source);
}

// A skill mirrored on several marketplaces is one row; any of its sources selects it.
export function filterBySource(
  results: readonly MarketplaceSkill[],
  filter: SourceFilter,
): MarketplaceSkill[] {
  if (filter === "all") return [...results];
  return results.filter((r) => listedIn(r, filter));
}

function oneDecimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** 812 → "812", 193 700 → "193.7K", 1 250 000 → "1.3M". */
export function formatCompactCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "0";
  if (n < 1000) return String(Math.round(n));
  // Round first so 999 960 reads "1M", not "1000K".
  if (Math.round(n / 100) < 10_000) return `${oneDecimal(n / 1000)}K`;
  return `${oneDecimal(n / 1_000_000)}M`;
}

export interface SourceChip {
  id: SourceFilter;
  label: string;
  // "12", or "no response" when the marketplace failed.
  status: string;
  ok: boolean;
  error?: string;
}

// One chip per marketplace in a fixed order, even one the relay did not report on.
export function sourceChips(
  results: readonly MarketplaceSkill[],
  sources: readonly MarketplaceSourceStatus[],
): SourceChip[] {
  const chips: SourceChip[] = [
    { id: "all", label: "All", status: String(results.length), ok: true },
  ];
  for (const id of MARKETPLACE_ORDER) {
    const reported = sources.find((s) => s.source === id);
    if (reported && !reported.ok) {
      chips.push({
        id,
        label: MARKETPLACE_LABELS[id],
        status: "no response",
        ok: false,
        ...(reported.error ? { error: reported.error } : {}),
      });
      continue;
    }
    const count = results.filter((r) => listedIn(r, id)).length;
    chips.push({ id, label: MARKETPLACE_LABELS[id], status: String(count), ok: true });
  }
  return chips;
}

export type SearchOutcome = "results" | "empty" | "all-failed";

export function searchOutcome(
  results: readonly MarketplaceSkill[],
  sources: readonly MarketplaceSourceStatus[],
): SearchOutcome {
  if (results.length > 0) return "results";
  if (sources.length > 0 && sources.every((s) => !s.ok)) return "all-failed";
  return "empty";
}

/** "3 of 4 marketplaces responded", or "" when every one answered. */
export function sourcesSummary(sources: readonly MarketplaceSourceStatus[]): string {
  const failed = sources.filter((s) => !s.ok);
  if (sources.length === 0 || failed.length === 0) return "";
  const ok = sources.length - failed.length;
  const names = failed.map((s) => MARKETPLACE_LABELS[s.source]).join(", ");
  return `${ok} of ${sources.length} marketplaces responded · no response: ${names}`;
}

/** One spelling per SKILL.md: https, raw.githubusercontent.com for a blob, no trailing slash. */
export function canonicalSkillUrl(url: string): string {
  const m = /^(?:https?:\/\/)([^/?#]+)([^?#]*)/i.exec(url.trim());
  if (!m) return url.trim().replace(/\/+$/, "");
  let host = m[1].toLowerCase().replace(/^www\./, "");
  let path = m[2];
  const blob = /^\/([^/]+)\/([^/]+)\/blob\/(.+)$/.exec(path);
  if (host === "github.com" && blob) {
    host = "raw.githubusercontent.com";
    path = `/${blob[1]}/${blob[2]}/${blob[3]}`;
  }
  return `https://${host}${path.replace(/\/+$/, "")}`;
}

// A pinned import matches the same repository and path, even when the branch advances.
function sourceIdentity(url: string, knownPath?: string): { path: string; pinned: boolean } {
  const canonical = canonicalSkillUrl(url).replace(
    /^(https:\/\/github\.com\/[^/]+\/[^/]+)\/tree\/(.+)$/,
    "$1/blob/$2/SKILL.md",
  );
  const raw = canonicalSkillUrl(canonical);
  const qualified =
    /^(https:\/\/raw\.githubusercontent\.com\/[^/]+\/[^/]+)\/refs\/(?:heads|tags)\//.exec(raw);
  if (qualified) {
    const suffix = knownPath ? `${knownPath.replace(/^\/+|\/+$/g, "")}/SKILL.md` : undefined;
    return {
      path: suffix && raw.endsWith(`/${suffix}`) ? `${qualified[1]}/${suffix}` : raw,
      pinned: false,
    };
  }
  const match = /^(https:\/\/raw\.githubusercontent\.com\/[^/]+\/[^/]+)\/([^/]+)\/(.+)$/.exec(raw);
  return match
    ? { path: `${match[1]}/${match[3]}`, pinned: /^[a-f0-9]{40}$/.test(match[2]) }
    : { path: raw, pinned: false };
}
export function importedSkillFor<T extends { id: string; sourceUrl?: string }>(
  result: MarketplaceSkill,
  skills: readonly T[],
  resolvedUrl?: string,
): T | undefined {
  if (!result.importUrl) return undefined;
  const original = canonicalSkillUrl(result.importUrl);
  const want = resolvedUrl ? canonicalSkillUrl(resolvedUrl) : original;
  const target = sourceIdentity(want, result.path);
  return skills.find((s) => {
    if (!s.sourceUrl) return false;
    const storedUrl = canonicalSkillUrl(s.sourceUrl);
    if (storedUrl === want || storedUrl === original) return true;
    const stored = sourceIdentity(s.sourceUrl, result.path);
    return (stored.pinned || target.pinned) && stored.path === target.path;
  });
}

export function metricsFor(result: MarketplaceSkill): string[] {
  const out: string[] = [];
  if (result.installs !== undefined) {
    out.push(`${formatCompactCount(result.installs)} installs`);
  }
  if (result.stars !== undefined) out.push(`${formatCompactCount(result.stars)} ★`);
  return out;
}

export function repoPath(result: MarketplaceSkill): string {
  return result.path ? `${result.repo}/${result.path.replace(/^\/+/, "")}` : result.repo;
}

// Names every marketplace that listed the row, not just the one that answered first.
export function onlyInLabel(result: MarketplaceSkill): string {
  const sources = [...new Set([result.source, ...result.alsoIn])].map((s) => MARKETPLACE_LABELS[s]);
  return `Only on ${LIST_FORMAT.format(sources)}`;
}

export async function skillImportChanges(
  stored: Pick<Skill, "content" | "files">,
  draft: MarketplaceDraft,
): Promise<string[]> {
  const changes: string[] = [];
  if (stored.content !== draft.content) changes.push("Changed: SKILL.md");
  const before = new Map((stored.files ?? []).map((file) => [file.path, file]));
  for (const file of draft.files) {
    const old = before.get(file.path);
    before.delete(file.path);
    if (!old) {
      changes.push(`Added: ${file.path}`);
      continue;
    }
    const bytes =
      file.encoding === "base64"
        ? Uint8Array.from(atob(file.content), (c) => c.charCodeAt(0))
        : new TextEncoder().encode(file.content);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    const sha = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
    if (sha !== old.sha256 || file.mode !== old.mode) changes.push(`Changed: ${file.path}`);
  }
  for (const path of before.keys()) changes.push(`Removed: ${path}`);
  return changes;
}
